import React, { useEffect, useState } from "react";
import { CheckCircle2, Circle, Clock, AlignLeft, MessageSquare, Paperclip, CheckSquare, Mic } from "lucide-react";
import { api } from "../../lib/api";
import { tagIndex, useTagFamilies } from "../DossierTags";
import { dueState } from "../TicketDue";
import { Avatar } from "./TicketMembers";
import { DossierPill } from "./DossierLinker";

const BAR = { red: "bg-red-500", orange: "bg-orange-500", amber: "bg-amber-400", emerald: "bg-emerald-500", teal: "bg-teal-500", sky: "bg-sky-500", indigo: "bg-indigo-500", violet: "bg-violet-400", rose: "bg-pink-400", slate: "bg-slate-400" };
const DATE_CLS = {
  overdue: "bg-red-100 text-red-700",
  soon: "bg-amber-300 text-amber-950",
  done: "bg-emerald-500 text-white",
  future: "text-norea-textMuted",
};
const short = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

const coverCache = new Map();
function useCover(basePath, ticket) {
  const img = !ticket.cover_hidden && (ticket.attachments || []).find((a) => (a.content_type || "").startsWith("image/"));
  const key = img ? `${ticket.id}|${img.id}` : null;
  const [src, setSrc] = useState(key ? coverCache.get(key) : null);
  useEffect(() => {
    if (!key || coverCache.has(key)) { setSrc(key ? coverCache.get(key) : null); return; }
    api.get(`${basePath}/${ticket.id}/attachments/${img.id}`, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); coverCache.set(key, u); setSrc(u); }).catch(() => {});
  }, [key, basePath, ticket.id, img]);
  return src;
}

export const LABELS_KEY = "norea_trello_labels_open";

function Labels({ ids, open, onToggle }) {
  const idx = tagIndex(useTagFamilies());
  const tags = (ids || []).map((i) => idx[i]).filter(Boolean);
  if (!tags.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mb-1.5" onClick={(e) => { e.stopPropagation(); onToggle(); }} onPointerDown={(e) => e.stopPropagation()} data-testid="trello-card-labels">
      {tags.map((t) => (
        <span key={t.id} title={t.name}
          className={`rounded transition-[width,height] duration-200 hover:brightness-95 cursor-pointer ${BAR[t.color] || BAR.slate} ${open ? "px-1.5 py-0.5 text-[10px] font-semibold text-white leading-tight" : "w-10 h-2"}`}
          data-testid={`trello-card-label-${t.id}`}>{open ? t.name : null}</span>
      ))}
    </div>
  );
}

function DateBadge({ t }) {
  const st = dueState(t);
  if (t.due_at) {
    const label = `${t.start_at ? `${short(t.start_at)} - ` : ""}${short(t.due_at)}`;
    return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${DATE_CLS[st]}`} data-testid={`trello-card-date-${t.id}`} data-state={st}><Clock className="w-3 h-3" /> {label}</span>;
  }
  if (t.start_at) {
    const started = new Date(t.start_at) <= new Date();
    return <span className="inline-flex items-center gap-1 text-[11px] text-norea-textMuted" data-testid={`trello-card-date-${t.id}`}><Clock className="w-3 h-3" /> {started ? "A commencé le" : "Commence le"} : {short(t.start_at)}</span>;
  }
  return null;
}

export default function TrelloCard({ ticket: t, basePath, labelsOpen, onToggleLabels, onOpen, onChanged, dragging }) {
  const cover = useCover(basePath, t);
  const cl = t.checklist || [];
  const clDone = cl.filter((c) => c.done).length;
  const nAtt = (t.attachments || []).length;
  const toggleDone = async (e) => {
    e.stopPropagation();
    await api.patch(`${basePath}/${t.id}/complete`, { done: !t.due_done });
    onChanged?.();
  };
  return (
    <div onClick={(e) => !e.defaultPrevented && onOpen?.()}
      className={`group bg-white rounded-lg mb-2 overflow-hidden cursor-pointer border transition-shadow ${dragging ? "shadow-2xl rotate-2 border-norea-primary" : "border-norea-border shadow-sm hover:shadow-md hover:border-norea-primary/40"}`}
      data-testid={`ticket-${t.id}`}>
      {cover && <img src={cover} alt="" className="w-full max-h-56 object-cover" draggable={false} data-testid={`trello-card-cover-${t.id}`} />}
      <div className="px-3 pt-2.5 pb-2">
        <Labels ids={t.tags} open={labelsOpen} onToggle={onToggleLabels} />
        <div className="flex items-start gap-1.5">
          <button onClick={toggleDone} onPointerDown={(e) => e.stopPropagation()}
            className={`mt-0.5 flex-shrink-0 transition-all ${t.due_done ? "w-4" : "w-0 opacity-0 group-hover:w-4 group-hover:opacity-100"}`}
            title={t.due_done ? "Marquer non terminé" : "Marquer terminé"} data-testid={`trello-card-done-${t.id}`}>
            {t.due_done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-norea-textMuted" />}
          </button>
          <div className={`text-sm font-medium leading-snug break-words ${t.due_done ? "text-norea-textMuted" : "text-norea-text"}`}>{t.title}</div>
        </div>
        {(t.dossiers || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1.5" onPointerDown={(e) => e.stopPropagation()}>{t.dossiers.map((d) => <DossierPill key={d.id} d={d} />)}</div>}
        <div className="flex items-center gap-2.5 flex-wrap mt-1.5 text-norea-textMuted">
          <DateBadge t={t} />
          {t.description && <AlignLeft className="w-3.5 h-3.5" title="Cette carte a une description" />}
          {t.audio_id && <Mic className="w-3.5 h-3.5" title="Ticket vocal" />}
          {t.comment_count > 0 && <span className="inline-flex items-center gap-0.5 text-[11px]" data-testid={`trello-card-comments-${t.id}`}><MessageSquare className="w-3.5 h-3.5" /> {t.comment_count}</span>}
          {nAtt > 0 && <span className="inline-flex items-center gap-0.5 text-[11px]" data-testid={`trello-card-atts-${t.id}`}><Paperclip className="w-3.5 h-3.5" /> {nAtt}</span>}
          {cl.length > 0 && (
            <span className={`inline-flex items-center gap-0.5 text-[11px] px-1 rounded ${clDone === cl.length ? "bg-emerald-500 text-white" : ""}`} data-testid={`ticket-${t.id}-checklist-badge`}>
              <CheckSquare className="w-3.5 h-3.5" /> {clDone}/{cl.length}
            </span>
          )}
          {(t.members || []).length > 0 && (
            <div className="ml-auto flex -space-x-1.5">
              {t.members.map((m) => <span key={m} className="ring-2 ring-white rounded-full" data-testid={`ticket-${t.id}-member`}><Avatar email={m} size="w-6 h-6 text-[9px]" /></span>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
